import React from 'react';
import SEO from '../components/SEO';
import CantoAtmosphere from '../components/CantoAtmosphere';
import PoetsBench from '../components/PoetsBench';
import ContextWeb from '../components/ContextWeb';
import ScreenplaySnap from '../components/ScreenplaySnap';
import StoryboardRun from '../components/StoryboardRun';
import { CantoMark, PaintedHeader, PAINTINGS, Placard } from '../components/paintings';
import useLantern from '../hooks/useLantern';
import MobileChatStage from './MobileChatStage';
import MobileProductStrip from './MobileProductStrip';
import virgilProfileMark from '../assets/virgil-profile-mark.webp';
import barqueOfDante from '../assets/barque-of-dante.webp';
import parnassusRaphael from '../assets/parnassus-raphael.webp';
import philosopherRembrandt from '../assets/philosopher-rembrandt.jpg';
import virgilsTombWright from '../assets/virgils-tomb-wright.webp';
import '../components/ModernHero.css';
import './MobileLandingPage.css';

// Same rotation as the desktop hero so both surfaces speak with one voice.
const MOBILE_HERO_BYLINES = [
  "Creativity is an inferno. Let Virgil guide you to paradise.",
  "From blank page to locked pages, without losing your voice.",
  "Screenplays, novels, podcasts, pitches, and visual beats in one flow.",
];

// Two fixed lines, so the list never wraps with one word left over.
const MOBILE_HERO_FORMATS = [
  ['Screenplays', 'Novels', 'Pitches'],
  ['Podcasts', 'Coverage'],
];

// Full-bleed painting chapters carried over from the desktop page.
const MOBILE_ART_CHAPTERS = {
  delacroix: {
    art: barqueOfDante,
    position: '50% 62%',
    eyebrow: 'Why “Virgil”',
    quote: (
      <>
        Every draft has its dark wood: the place where the story stalls and the way forward disappears.
        <br />
        Virgil is named for the guide who walks you out.
      </>
    ),
    painting: PAINTINGS.delacroix,
  },
  parnassus: {
    art: parnassusRaphael,
    position: '50% 50%',
    eyebrow: "The Poets' Bench",
    quote: (
      <>
        Writers have argued about craft for two thousand years. Pull up a chair.
      </>
    ),
    painting: PAINTINGS.parnassus,
  },
  virgilTomb: {
    art: virgilsTombWright,
    position: '48% 52%',
    eyebrow: 'The oldest collaboration',
    quote: (
      <>
        Dante had one.
        <br />
        Milton wanted one.
        <br />
        Now every writer <span className="nowrap">gets a Virgil.</span>
      </>
    ),
    painting: PAINTINGS.virgilTomb,
  },
};

const MOBILE_MISSION_PILLARS = [
  {
    kicker: 'Clarity',
    title: 'Focus the creative signal',
    copy: 'Fewer distractions, sharper hierarchy, and surfaces that help you decide what matters next.',
  },
  {
    kicker: 'Momentum',
    title: 'Keep the draft moving',
    copy: 'Formatting, rewrites, ideation, and scene building should feel like one uninterrupted motion.',
  },
  {
    kicker: 'Authorship',
    title: 'Protect the writer on the page',
    copy: 'The tool can suggest, generate, and challenge, but the voice remains unmistakably yours.',
  },
];

const MobileOpenAiMark = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" fillRule="evenodd" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M9.205 8.658v-2.26c0-.19.072-.333.238-.428l4.543-2.616c.619-.357 1.356-.523 2.117-.523 2.854 0 4.662 2.212 4.662 4.566 0 .167 0 .357-.024.547l-4.71-2.759a.797.797 0 00-.856 0l-5.97 3.473zm10.609 8.8V12.06c0-.333-.143-.57-.429-.737l-5.97-3.473 1.95-1.118a.433.433 0 01.476 0l4.543 2.617c1.309.76 2.189 2.378 2.189 3.948 0 1.808-1.07 3.473-2.76 4.163zM7.802 12.703l-1.95-1.142c-.167-.095-.239-.238-.239-.428V5.899c0-2.545 1.95-4.472 4.591-4.472 1 0 1.927.333 2.712.928L8.23 5.067c-.285.166-.428.404-.428.737v6.898zM12 15.128l-2.795-1.57v-3.33L12 8.658l2.795 1.57v3.33L12 15.128zm1.796 7.23c-1 0-1.927-.332-2.712-.927l4.686-2.712c.285-.166.428-.404.428-.737v-6.898l1.974 1.142c.167.095.238.238.238.428v5.233c0 2.545-1.974 4.472-4.614 4.472zm-5.637-5.303l-4.544-2.617c-1.308-.761-2.188-2.378-2.188-3.948A4.482 4.482 0 014.21 6.327v5.423c0 .333.143.571.428.738l5.947 3.449-1.95 1.118a.432.432 0 01-.476 0zm-.262 3.9c-2.688 0-4.662-2.021-4.662-4.519 0-.19.024-.38.047-.57l4.686 2.71c.286.167.571.167.856 0l5.97-3.448v2.26c0 .19-.07.333-.237.428l-4.543 2.616c-.619.357-1.356.523-2.117.523zm5.899 2.83a5.947 5.947 0 005.827-4.756C22.287 18.339 24 15.84 24 13.296c0-1.665-.713-3.282-1.998-4.448.119-.5.19-.999.19-1.498 0-3.401-2.759-5.947-5.946-5.947-.642 0-1.26.095-1.88.31A5.962 5.962 0 0010.205 0a5.947 5.947 0 00-5.827 4.757C1.713 5.447 0 7.945 0 10.49c0 1.666.713 3.283 1.998 4.448-.119.5-.19 1-.19 1.499 0 3.401 2.759 5.946 5.946 5.946.642 0 1.26-.095 1.88-.309a5.96 5.96 0 004.162 1.713z" />
  </svg>
);

const MobileOpenSourceMark = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M9 6l-6 6 6 6M15 6l6 6-6 6" stroke="#F0BB8A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MOBILE_MUSES = [
  {
    icon: <img src="/icons/model_icons/claude-color.svg" alt="Claude" />,
    title: 'Anthropic',
    subtitle: 'Claude Opus 5.5',
    tagline: 'A brilliant writer. A master orchestrator.',
    desc: 'Anthropic’s new Opus 5.5 opens up the creative process from every angle. It pairs a storyteller’s ear for dialogue, subtext, and character with the orchestration skills to carry an ambitious project beyond the page. Develop the idea, shape the script, plan the shots, and direct the next move across Virgil’s creative tools.',
  },
  {
    icon: <MobileOpenAiMark />,
    title: 'OpenAI',
    subtitle: 'GPT-6.1 Sol Ultra',
    tagline: 'Big ideas. Deep reasoning. A lighter price.',
    desc: 'Sol turns scattered notes into architecture: bold outlines, richly imagined worlds, and rewrites that hold the whole story together. GPT-6.1 brings near-Astra capability at a fraction of Astra’s token cost. Ultra turns reasoning all the way up. When the next move isn’t obvious, this is the mind you want on it.',
  },
  {
    icon: <img src="/icons/model_icons/gemini-color.svg" alt="Gemini" />,
    title: 'Google',
    subtitle: 'Gemini 3.1 Pro',
    tagline: 'Best for huge context, visual reference boards, and efficient coverage.',
    desc: 'Gemini 3.1 Pro gives Virgil a broad, practical pass when you want scripts, bibles, notes, images, and research considered together without using flagship energy on every iteration.',
  },
  {
    icon: <img src="/icons/model_icons/grok.svg" alt="Grok" className="icon-grok" />,
    title: 'xAI',
    subtitle: 'Grok 4.7',
    tagline: 'Explore sharper dialogue, alternatives, and critique.',
    desc: 'Grok 4.7 is Virgil’s xAI default for exploring punchier dialogue, contrarian options, and direct notes. Use it to challenge a scene, test a new angle, or compare a different take on your draft.',
  },
  {
    icon: <MobileOpenSourceMark />,
    title: 'Open Source',
    subtitle: 'DeepSeek V4 Pro, Kimi K3 & more',
    tagline: 'Best for high-volume exploration without breaking the bank.',
    desc: 'Open models give Virgil room to generate alternates: quick summaries, low-stakes rewrites, draft experiments, and options you can sift through fast.',
  },
];

const MOBILE_MODEL_LINEUP = [
  { label: 'Claude Opus 5.5' },
  { label: 'GPT-6.1 Sol Ultra' },
  { label: 'Gemini 3.1 Pro' },
  { label: 'Grok 4.7' },
  { label: 'DeepSeek V4 Pro' },
  { label: 'Kimi K3' },
  { kind: 'separator', label: '/' },
  { label: 'Seedance 2.5', kind: 'media' },
  { label: 'Gemini Omni 1.1 Flash', kind: 'media' },
  { label: 'Veo 3.1', kind: 'media' },
  { label: 'Kling 3.0 Turbo', kind: 'media' },
  { label: 'MiniMax H3', kind: 'media' },
  { label: 'Luma Ray2', kind: 'media' },
  { label: 'Runway Gen-4.5', kind: 'media' },
  { label: 'FLUX 3 Video', kind: 'media' },
  { label: 'FLUX 3 Image', kind: 'media' },
  { label: 'Nano Banana 2.1', kind: 'media' },
  { label: 'Seedream 5.0 Pro', kind: 'media' },
  { label: 'Seedream 5.0 Flash', kind: 'media' },
  { label: 'Recraft V4.1', kind: 'media' },
  { label: 'Ideogram 4.5 Precise Edit', kind: 'media' },
  { label: 'Gemini 3.8 Flash TTS', kind: 'media' },
  { label: 'Eleven v4', kind: 'media' },
];

const MOBILE_STUDIO_HANDLES = [
  {
    title: 'Context that holds.',
    copy: 'Large drafts, character webs, tone notes, scene fragments, and research stay close at hand, so you can keep making decisions with the whole story in view.',
    tint: '#F08B5B',
    graphic: ContextWeb,
    icon: (
      <svg width="20" height="20" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="7" y="8" width="12" height="14" rx="3.5" fill="rgba(240, 139, 91, 0.12)" stroke="#F08B5B" strokeWidth="1.4" />
        <path d="M10 12h6M10 15.5h8M10 19h5" stroke="#F0BB8A" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M11 6h10a3 3 0 0 1 3 3v10" stroke="#F08B5B" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: 'Format-native craft.',
    copy: 'Screenplay, pilot, novel, treatment, podcast, pitch deck, or coverage pass. Virgil adapts the surface to the kind of work you are actually doing.',
    tint: '#E2B84A',
    graphic: ScreenplaySnap,
    icon: (
      <svg width="20" height="20" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 5h7l4 4v10a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V9a4 4 0 0 1 4-4Z" fill="rgba(226, 184, 74, 0.1)" stroke="#E2B84A" strokeWidth="1.4" />
        <path d="M16 5v4h4" stroke="#E2B84A" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9.5 14h9M9.5 17.5h9M9.5 21h6" stroke="#F0D48A" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: 'Generative momentum.',
    copy: 'Draft scenes, punch dialogue, map beats, generate storyboards, and iterate visuals without jumping between disconnected tools.',
    tint: '#F3B15E',
    graphic: StoryboardRun,
    icon: (
      <svg width="20" height="20" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 20L20 8" stroke="#F3B15E" strokeWidth="2" strokeLinecap="round" />
        <path d="M14 8h6v6" stroke="#F3B15E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="8" cy="9" r="1.5" fill="#FFD79E" />
        <circle cx="11.5" cy="6" r="1" fill="#FFD79E" />
        <circle cx="20" cy="19.5" r="1.5" fill="#FFD79E" />
      </svg>
    ),
  },
];

// One Inferno project, carried through the complete creative pipeline.
const MOBILE_FEATURE_GALLERY = [
  {
    type: 'projects',
    eyebrow: '01 / Projects',
    title: 'Your work, wherever you are',
    description: 'Cloud-synced projects keep every draft and visual ready on desktop or on the go.',
  },
  {
    type: 'idea',
    eyebrow: '02 / Writing',
    title: 'Start with an idea',
    description: 'Write in your own voice while Virgil helps you explore the next move.',
  },
  {
    type: 'storyboard',
    eyebrow: '03 / Storyboard',
    title: 'Turn it into a storyboard',
    description: 'Translate pages into visual beats without losing the creative thread.',
  },
  {
    type: 'video',
    eyebrow: '04 / Video',
    title: 'Bring your vision to life',
    description: 'Carry those shots into a working timeline and shape the final cut.',
    badge: 'Beta',
  },
];

// A full-bleed painting with a single line of copy set over it. Cream type
// on a dark veil in both themes, exactly as the desktop chapters read. The
// placard sits along the bottom edge of the painting.
function MobileArtChapter({
  chapter,
  id,
  canto,
  align = 'center',
  size = 'tall',
  lantern = false,
  className = '',
  children,
  ...rest
}) {
  const sectionRef = React.useRef(null);
  useLantern(sectionRef, lantern);

  return (
    <section
      ref={sectionRef}
      id={id}
      className={`mobile-art-chapter mobile-art-chapter--${size} mobile-art-chapter--align-${align} ${lantern ? 'has-lantern' : ''} ${className} mobile-reveal mobile-reveal--fade`}
      aria-label={typeof chapter.eyebrow === 'string' ? chapter.eyebrow : undefined}
      {...rest}
    >
      <img
        src={chapter.art}
        alt=""
        className="mobile-art-chapter-art"
        style={{ objectPosition: chapter.position }}
        loading="lazy"
        decoding="async"
        aria-hidden="true"
      />
      <div className="mobile-art-chapter-veil" aria-hidden="true" />
      {lantern && <div className="art-lantern" aria-hidden="true" />}
      <div className="mobile-art-chapter-inner">
        {canto && <CantoMark numeral={canto} />}
        <span className="eyebrow mobile-art-chapter-eyebrow">{chapter.eyebrow}</span>
        <p className="mobile-art-chapter-quote">{chapter.quote}</p>
        {children}
      </div>
      {chapter.painting && <Placard painting={chapter.painting} className="mobile-art-chapter-placard" />}
    </section>
  );
}

function MobileMusesCarousel() {
  const [activeIdx, setActiveIdx] = React.useState(0);
  const touchStart = React.useRef(0);

  const handleTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) <= 50) return;
    if (diff > 0 && activeIdx < MOBILE_MUSES.length - 1) setActiveIdx((i) => i + 1);
    if (diff < 0 && activeIdx > 0) setActiveIdx((i) => i - 1);
  };

  return (
    <div className="muses-carousel-wrapper">
      <div
        className="muses-carousel"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="muses-carousel-track"
          style={{ transform: `translateX(-${activeIdx * 100}%)` }}
        >
          {MOBILE_MUSES.map((muse, index) => (
            <div
              key={muse.title}
              className="muse-slide"
              aria-hidden={index === activeIdx ? undefined : true}
              inert={index === activeIdx ? undefined : ''}
            >
              <div className="feature-card">
                <div className="feature-icon">{muse.icon}</div>
                <h3>
                  {muse.title}
                  <span className="muse-subtitle">{muse.subtitle}</span>
                </h3>
                <p className="muse-tagline">{muse.tagline}</p>
                <p>{muse.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="muses-nav">
          <button
            type="button"
            className="muse-arrow muse-arrow-left"
            onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
            disabled={activeIdx === 0}
            aria-label="Previous model"
          >
            ‹
          </button>
          <div className="muses-dots">
            {MOBILE_MUSES.map((muse, index) => (
              <button
                type="button"
                key={muse.title}
                className={`muse-dot ${index === activeIdx ? 'active' : ''}`}
                onClick={() => setActiveIdx(index)}
                aria-label={`Go to ${muse.title}`}
              />
            ))}
          </div>
          <button
            type="button"
            className="muse-arrow muse-arrow-right"
            onClick={() => setActiveIdx((i) => Math.min(MOBILE_MUSES.length - 1, i + 1))}
            disabled={activeIdx === MOBILE_MUSES.length - 1}
            aria-label="Next model"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}

// Reveal sections as they scroll into view. The page only opts into the
// hidden starting state once the observer is attached, so content is never
// stuck invisible if IntersectionObserver is missing or JS is slow.
function useScrollReveal(rootRef) {
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof window === 'undefined') return undefined;

    const prefersReducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targets = Array.from(root.querySelectorAll('.mobile-reveal'));
    if (!targets.length || prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      targets.forEach((el) => el.classList.add('is-in'));
      return undefined;
    }

    root.classList.add('js-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -4% 0px', threshold: 0.01 }
    );
    targets.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [rootRef]);
}

function MobileLandingPage() {
  const rootRef = React.useRef(null);
  const [bylineIndex, setBylineIndex] = React.useState(0);
  const [bylineVisible, setBylineVisible] = React.useState(true);

  useScrollReveal(rootRef);

  React.useEffect(() => {
    let swapTimeout;
    const id = window.setInterval(() => {
      setBylineVisible(false);
      swapTimeout = window.setTimeout(() => {
        setBylineIndex((i) => (i + 1) % MOBILE_HERO_BYLINES.length);
        setBylineVisible(true);
      }, 600);
    }, 5000);

    return () => {
      window.clearInterval(id);
      if (swapTimeout) window.clearTimeout(swapTimeout);
    };
  }, []);

  return (
    <div className="mobile-landing-page" ref={rootRef}>
      <SEO
        title="Virgil | AI Writing Workspace for Screenplays and Novels"
        description="Outline, draft, revise, format, and explore visual ideas with multiple AI models in one workspace built for screenplays, novels, podcasts, and pitches."
        keywords="AI creative writing, screenwriting AI, novel writing AI, screenplay formatting, multi-model AI writer"
      />
      <CantoAtmosphere />

      <div className="mobile-landing-main">
        <section className="mobile-hero">
          <div className="mobile-hero-background" aria-hidden="true" />
          <div className="mobile-hero-glow-orb mobile-hero-glow-orb-1" aria-hidden="true" />
          <div className="mobile-hero-glow-orb mobile-hero-glow-orb-2" aria-hidden="true" />
          <div className="mobile-hero-content">
            <div className="mobile-hero-logo-wrap">
              <div className="mobile-hero-logo hero-medallion hero-medallion--solo" role="img" aria-label="Virgil">
                <img
                  src={virgilProfileMark}
                  alt=""
                  className="hero-medallion-face hero-medallion-profile"
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                  aria-hidden="true"
                />
              </div>
            </div>
            <h1 className="mobile-hero-title">
              <span className="mobile-hero-title-gradient heading-gradient">Meet Virgil</span>
            </h1>
            <p className="mobile-hero-subtitle">Your Storytelling OS</p>
            <div className="mobile-hero-byline-container">
              <p
                className="mobile-hero-byline"
                key={bylineIndex}
                style={{
                  animation: bylineVisible
                    ? 'bylineIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards'
                    : 'bylineOut 0.5s cubic-bezier(0.7, 0, 0.84, 0) forwards',
                }}
              >
                {MOBILE_HERO_BYLINES[bylineIndex]}
              </p>
            </div>
            <div className="mobile-hero-actions fade-seq-3">
              <button
                type="button"
                className="mobile-hero-btn mobile-hero-btn-primary"
                onClick={() => {
                  window.location.href = '/signup';
                }}
              >
                <span className="mobile-hero-btn-text">Start your 14-day trial</span>
                <span className="mobile-hero-btn-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="none">
                    <path d="M6 14L14 6" />
                    <path d="M7 6H14V13" />
                  </svg>
                </span>
              </button>
              <button
                type="button"
                className="mobile-hero-btn mobile-hero-btn-secondary"
                onClick={() => {
                  window.location.href = '/download';
                }}
              >
                <span className="mobile-hero-btn-text">Download</span>
                <span className="mobile-hero-btn-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="none">
                    <path d="M6 10H14" />
                    <path d="M10 6L14 10L10 14" />
                  </svg>
                </span>
              </button>
            </div>

            <div className="mobile-hero-formats fade-seq-4" aria-label="What you can write in Virgil">
              {MOBILE_HERO_FORMATS.map((line) => (
                <span key={line[0]} className="mobile-hero-formats-line">
                  {line.map((format, index) => (
                    <React.Fragment key={format}>
                      {index > 0 && <span className="mobile-hero-formats-dot" aria-hidden="true" />}
                      <span className="mobile-hero-format">{format}</span>
                    </React.Fragment>
                  ))}
                </span>
              ))}
            </div>

            <div className="hero-models mobile-hero-models fade-seq-5">
              <div className="model-carousel" aria-label="Model providers">
                <div className="model-carousel-track">
                  {[1, 2, 3].map((cycle) => (
                    <React.Fragment key={cycle}>
                      {MOBILE_MODEL_LINEUP.map((model, index) =>
                        model.kind === 'separator' ? (
                          <span key={`${cycle}-${index}-${model.label}`} className="model-sep" aria-hidden="true">
                            {model.label}
                          </span>
                        ) : (
                          <span
                            key={`${cycle}-${index}-${model.label}`}
                            className={`model-badge ${model.kind === 'media' ? 'media' : ''}`}
                          >
                            {model.label}
                          </span>
                        )
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <div className="model-carousel-copy">
                All writing, image, video, and voice models shown here are available in the public beta.
              </div>
            </div>
          </div>
        </section>

        <MobileChatStage />

        <section className="mobile-landing-section mobile-studio-handles-section">
          {/* One slab, read as an index: a real heading, then three numbered
              entries divided by hairlines, each with its own tinted icon. */}
          <div className="mobile-block mobile-reveal">
            <div className="mobile-block-heading">
              <p className="mobile-section-eyebrow">What the studio handles</p>
              <h2 className="mobile-section-title">One studio for the whole draft</h2>
              <p className="mobile-section-body">
                Three things Virgil keeps in hand so you can keep writing.
              </p>
            </div>
            <ol className="mobile-block-list">
              {MOBILE_STUDIO_HANDLES.map((item, index) => (
                <li
                  key={item.title}
                  className="mobile-block-row mobile-studio-handle-row"
                  style={{ '--tint': item.tint }}
                >
                  <div className="mobile-studio-handle-lead">
                    <div className="mobile-studio-handle-icon" aria-hidden="true">
                      {item.icon}
                    </div>
                    <span className="mobile-studio-handle-ordinal" aria-hidden="true">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="mobile-studio-handle-copy">
                    <h3>{item.title}</h3>
                    <p>{item.copy}</p>
                    {item.graphic && <item.graphic />}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="screenshots" className="mobile-landing-section mobile-feature-gallery-section">
          <div className="mobile-block mobile-reveal">
            <div className="mobile-block-heading">
              <CantoMark numeral="I" />
              <p className="mobile-section-eyebrow">Inside the studio</p>
              <h2 className="mobile-section-title">From first idea to final cut</h2>
              <p className="mobile-section-body">
                One continuous creative pipeline. Your project, your voice, and every step stay together.
              </p>
            </div>
            <MobileProductStrip items={MOBILE_FEATURE_GALLERY} />
          </div>
        </section>

        <MobileArtChapter chapter={MOBILE_ART_CHAPTERS.delacroix} id="why-virgil" canto="II" size="tall" lantern />

        {/* Night into day in acts, as on desktop. Keep a painting between two
            acts: its edges hide the seam between their grounds. */}
        <div className="canto-act canto-act--dawn" data-act="dawn">
          <section id="intro-copy" className="mobile-landing-section mobile-mission-section">
            <div className="mobile-block mobile-reveal">
              <div className="mobile-block-heading">
                <CantoMark numeral="III" />
                <p className="mobile-section-eyebrow">Our mission</p>
                <h2 className="mobile-section-title">Put serious storytelling tools in more hands</h2>
                <p className="mobile-section-body">
                  We build tools that help writers finish work without flattening the voice that makes it theirs.
                </p>
              </div>
              <div className="mobile-block-list">
                {MOBILE_MISSION_PILLARS.map((pillar) => (
                  <article key={pillar.title} className="mobile-block-row mobile-mission-pillar">
                    <span className="mobile-mission-pillar-kicker">{pillar.kicker}</span>
                    <h3>{pillar.title}</h3>
                    <p>{pillar.copy}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </div>

        <MobileArtChapter chapter={MOBILE_ART_CHAPTERS.parnassus} id="poets-bench" canto="IV" size="tall">
          <PoetsBench layout="stack" />
        </MobileArtChapter>

        <div className="canto-act canto-act--day" data-act="day">
          <section id="features" className="mobile-landing-section mobile-muses-section daylight">
            <PaintedHeader
              art={philosopherRembrandt}
              painting={PAINTINGS.philosopher}
              eyebrow="A mind at work"
              quote="Different questions demand different kinds of intelligence."
              className="painted-header--mobile"
            />
            <div className="mobile-block mobile-reveal">
              <div className="mobile-block-heading">
                <CantoMark numeral="V" />
                <p className="mobile-section-eyebrow">Model guides</p>
                <h2 className="mobile-section-title">Meet your muses</h2>
                <p className="mobile-section-body mobile-muses-intro">
                  Choose the AI guide that fits the creative pass: structure, voice, visuals, critique,
                  or fast exploration.
                </p>
              </div>
              <MobileMusesCarousel />
              <p className="mobile-section-body mobile-muses-outro">
                Pick the model for the pass, then bring every suggestion back into your own voice.
              </p>
            </div>
          </section>

          {/* The last painting is also the close, and where the dock bows out. */}
          <MobileArtChapter
            chapter={MOBILE_ART_CHAPTERS.virgilTomb}
            id="closing"
            canto="VI"
            align="left"
            size="band"
            className="mobile-art-chapter--finale"
            data-dock-stop=""
          >
            <p className="mobile-art-chapter-lede">Outline, draft, revise, storyboard, and polish in one place.</p>
            <div className="mobile-art-chapter-actions">
              <button
                type="button"
                className="mobile-hero-btn mobile-hero-btn-primary"
                onClick={() => {
                  window.location.href = '/signup';
                }}
              >
                Start your 14-day trial
              </button>
              <button
                type="button"
                className="mobile-hero-btn mobile-hero-btn-secondary"
                onClick={() => {
                  window.location.href = '/download';
                }}
              >
                Download
              </button>
            </div>
          </MobileArtChapter>
        </div>
      </div>
    </div>
  );
}

export default MobileLandingPage;
